import React from "react"

import { parseISO } from "date-fns"
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  GitBranch,
  Loader2,
  Lock,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Truck,
  Upload,
  UserRound,
} from "lucide-react"

import {
  type ChangeEvent,
  type ChangeEventIcon,
  type FormattedChange,
  type ImpactChip,
  type TimelineItem,
  actorLabel,
  actorProvenanceNote,
  changeEventIcon,
  changeEventLabel,
  changeEventTone,
  formattedChanges,
  impactChips,
  impactWindowLabel,
  rowSummary,
  splitFollowOns,
  stopChangeLine,
  subjectLabel,
} from "@/lib/changeEvents"
import { formatDateTimeLong, formatDateTimeShort } from "@/lib/dateFormats"
import { type Tone, toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"

/** Change-history feed shared by the monitoring route panel and the planning lineage view. */

const ICONS: Record<ChangeEventIcon, React.ComponentType<{ size?: number | string }>> = {
  created: Plus,
  removed: Trash2,
  retimed: Clock,
  reordered: ArrowRight,
  renamed: Pencil,
  person: UserRound,
  vehicle: Truck,
  calendar: CalendarDays,
  lock: Lock,
  stop: MapPin,
  cascade: GitBranch,
  export: Upload,
  generic: CircleDot,
}

const Gutter: React.FC<{ tone: Tone; icon: ChangeEventIcon; last: boolean }> = ({
  tone,
  icon,
  last,
}) => {
  const t = toneClasses(tone)
  const Icon = ICONS[icon]
  return (
    <div className="flex w-[26px] flex-none flex-col items-center">
      <div
        className={cn(
          "flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full border",
          t.badge
        )}
      >
        <Icon size={13} />
      </div>
      {!last && <div className="mt-1.5 w-px flex-1 bg-palette-jungleGreen200" />}
    </div>
  )
}

const FieldDiffRow: React.FC<{ change: FormattedChange }> = ({ change }) => {
  const delta = change.deltaMinutes
  const deltaTone: Tone = delta === null ? "neutral" : delta > 0 ? "warning" : "success"
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <span className="w-[74px] flex-none text-palette-jungleGreen600">{change.label}</span>
      <span className="text-palette-jungleGreen600 line-through">{change.before}</span>
      <ArrowRight size={10} className="flex-none text-palette-jungleGreen400" />
      <span className={cn("font-semibold", toneClasses("success").onSurface)}>{change.after}</span>
      {delta !== null && delta !== 0 && (
        <span
          className={cn(
            "ml-auto flex-none rounded border px-1.5 py-px text-[10px] font-medium",
            toneClasses(deltaTone).badge
          )}
        >
          {delta > 0 ? "+" : ""}
          {delta} min
        </span>
      )}
    </div>
  )
}

const ImpactStrip: React.FC<{ chips: ImpactChip[]; windowLabel: string | null }> = ({
  chips,
  windowLabel,
}) => {
  if (chips.length === 0 && !windowLabel) return null
  const shown = chips.slice(0, 12)
  const rest = chips.length - shown.length

  return (
    <div className="mt-2.5 border-t border-palette-jungleGreen200 pt-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-palette-jungleGreen500">
        Dates changed by this edit
      </div>
      {shown.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {shown.map((chip) => (
            <span
              key={chip.date}
              className={cn(
                "rounded border px-1.5 py-px text-[10px] font-medium",
                toneClasses(chip.tone).badge
              )}
            >
              {chip.label}
              {chip.action !== "changed" && ` · ${chip.action}`}
            </span>
          ))}
          {rest > 0 && (
            <span className="text-[10px] text-palette-jungleGreen500">+ {rest} more</span>
          )}
        </div>
      )}
      {windowLabel && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-palette-jungleGreen600">
          <Clock size={12} className="mt-px flex-none text-palette-jungleGreen400" />
          {windowLabel}
        </div>
      )}
    </div>
  )
}

export const ChangeEventRow: React.FC<{
  event: ChangeEvent
  /** The events this one caused. Not named `children`: React reserves that. */
  followOns?: ChangeEvent[]
  last?: boolean
  /** Off inside a single route's own history. */
  showSubject?: boolean
}> = ({ event, followOns = [], last = false, showSubject = false }) => {
  const tone = changeEventTone(event)
  const changes = formattedChanges(event)
  const summary = rowSummary(event)
  const chips = impactChips(event, followOns)
  const { stops, routeDates } = splitFollowOns(followOns)
  const occurred = parseISO(event.occurred_at)
  const provenance = actorProvenanceNote(event)
  const detail = [actorLabel(event), showSubject ? subjectLabel(event) : null]
    .filter(Boolean)
    .join(" · ")

  const hasBody =
    changes.length > 0 || !!summary || chips.length > 0 || stops.length > 0 || !!event.error

  return (
    <div className="flex gap-3">
      <Gutter tone={tone} icon={changeEventIcon(event.event_type)} last={last} />
      <div className={cn("min-w-0 flex-1", last ? "" : "pb-3.5")}>
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold leading-snug text-palette-jungleGreen900">
            {changeEventLabel(event.event_type)}
          </span>
          {provenance && (
            <span className="flex-none text-[10px] text-palette-jungleGreen400">{provenance}</span>
          )}
          {/* Absolute time, so it can be compared against the route's own clock times. */}
          <span
            className="ml-auto flex-none font-mono text-[11px] text-palette-jungleGreen500"
            title={formatDateTimeLong(occurred)}
          >
            {formatDateTimeShort(occurred)}
          </span>
        </div>
        <div className="mt-0.5 text-[11px] text-palette-jungleGreen500">{detail}</div>

        {hasBody && (
          <div
            className={cn(
              "mt-2 rounded-lg border p-2.5",
              tone === "info" || tone === "neutral"
                ? "border-palette-jungleGreen200 bg-palette-jungleGreen000"
                : cn(toneClasses(tone).border, toneClasses(tone).surface)
            )}
          >
            {summary && (
              <div className="text-[11px] leading-relaxed text-palette-jungleGreen700">
                {summary}
              </div>
            )}
            {changes.length > 0 && (
              <div className="space-y-1.5">
                {changes.map((change) => (
                  <FieldDiffRow key={change.field} change={change} />
                ))}
              </div>
            )}
            {event.error && (
              <div
                className={cn(
                  "text-[11px] leading-relaxed",
                  changes.length > 0 && "mt-2 border-t border-palette-jungleGreen200 pt-2",
                  toneClasses("danger").onSurface
                )}
              >
                {event.error}
              </div>
            )}
            {stops.length > 0 && (
              <div
                className={cn(
                  changes.length > 0 || summary
                    ? "mt-2.5 border-t border-palette-jungleGreen200 pt-2.5"
                    : ""
                )}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-palette-jungleGreen500">
                  Stops changed
                </div>
                <div className="mt-1.5 space-y-1">
                  {stops.map((stop) => (
                    <div
                      key={stop.id}
                      className="text-[11px] leading-relaxed text-palette-jungleGreen700"
                    >
                      {stopChangeLine(stop)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <ImpactStrip chips={chips} windowLabel={impactWindowLabel(event)} />
            {routeDates.length > 0 && (
              <div className="mt-2.5 border-t border-palette-jungleGreen200 pt-2 text-[11px] text-palette-jungleGreen600">
                {routeDates.length} route{routeDates.length === 1 ? "" : "s"} rebuilt or cancelled
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const ExportRow: React.FC<{ item: Extract<TimelineItem, { kind: "export" }>; last: boolean }> = ({
  item,
  last,
}) => {
  const failed = item.export.status === "error"
  const tone: Tone = failed ? "danger" : "success"
  const at = parseISO(item.export.attempted_at)
  const system = item.export.system === "samsara" ? "Samsara" : "Track-POD"

  return (
    <div className="flex gap-3">
      <Gutter tone={tone} icon="export" last={last} />
      <div className={cn("min-w-0 flex-1", last ? "" : "pb-3.5")}>
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold leading-snug text-palette-jungleGreen900">
            {failed ? `${system} push failed` : `Pushed to ${system}`}
          </span>
          <span
            className="ml-auto flex-none font-mono text-[11px] text-palette-jungleGreen500"
            title={formatDateTimeLong(at)}
          >
            {formatDateTimeShort(at)}
          </span>
        </div>
        <div className="mt-0.5 text-[11px] text-palette-jungleGreen500">
          {item.export.attempted_by ?? `Automatic · ${item.export.trigger_source}`}
        </div>
        {item.export.error && (
          <div
            className={cn(
              "mt-2 rounded-lg border p-2.5 text-[11px] leading-relaxed",
              toneClasses("danger").border,
              toneClasses("danger").surface,
              toneClasses("danger").onSurface
            )}
          >
            {item.export.error}
          </div>
        )}
      </div>
    </div>
  )
}

export const RouteTimeline: React.FC<{
  items: TimelineItem[]
  emptyLabel?: string
}> = ({ items, emptyLabel = "No changes recorded yet" }) => {
  if (items.length === 0) {
    return <div className="text-sm italic text-palette-jungleGreen400">{emptyLabel}</div>
  }
  return (
    <div>
      {items.map((item, index) => {
        const last = index === items.length - 1
        return item.kind === "export" ? (
          <ExportRow key={item.id} item={item} last={last} />
        ) : (
          <ChangeEventRow key={item.id} event={item.event} last={last} />
        )
      })}
    </div>
  )
}

export default RouteTimeline

export const HistoryDisclosure: React.FC<{
  label?: string
  count?: number | null
  isLoading?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  headerClassName?: string
  bodyClassName?: string
  children: React.ReactNode
}> = ({
  label = "Change history",
  count = null,
  isLoading = false,
  open,
  onOpenChange,
  headerClassName,
  bodyClassName,
  children,
}) => (
  <>
    <button
      type="button"
      onClick={() => onOpenChange(!open)}
      aria-expanded={open}
      className={cn("flex w-full items-center gap-2 text-left", headerClassName)}
    >
      {open ? (
        <ChevronDown size={13} className="flex-none text-palette-jungleGreen500" />
      ) : (
        <ChevronRight size={13} className="flex-none text-palette-jungleGreen500" />
      )}
      <GitBranch size={13} className="flex-none text-palette-jungleGreen500" />
      <span className="text-xs font-semibold uppercase tracking-wide text-palette-jungleGreen500">
        {label}
      </span>
      {isLoading && <Loader2 size={12} className="animate-spin text-palette-jungleGreen400" />}
      {!isLoading && count !== null && count > 0 && (
        <span className="ml-auto flex-none text-[11px] text-palette-jungleGreen500">
          {count} change{count === 1 ? "" : "s"}
        </span>
      )}
    </button>
    {open && <div className={bodyClassName}>{children}</div>}
  </>
)
